<script setup lang="ts">
import { useAccountStore } from '@/store/account'
import { getImageUrl } from '@/utils'
import dayjs from 'dayjs'
import { Divider, ProgressBar } from 'primevue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  item: any
}>()

const accountStore = useAccountStore()
const stepOpen = ref(false)
const isHidden = ref(false)

// 缓存相关
function getCacheKey() {
  // 使用 sender 和 nonce 的组合作为唯一标识符
  return `withdraw_deleted_${props.item.sender}_${props.item.nonce}`
}

function checkIfDeleted() {
  const cacheKey = getCacheKey()
  const deletedItems = JSON.parse(localStorage.getItem('withdraw_deleted_items') || '{}')
  return deletedItems[cacheKey] === true
}

function markAsDeleted() {
  const cacheKey = getCacheKey()
  const deletedItems = JSON.parse(localStorage.getItem('withdraw_deleted_items') || '{}')
  deletedItems[cacheKey] = true
  localStorage.setItem('withdraw_deleted_items', JSON.stringify(deletedItems))
}

// 区块相关
const blockNumber = ref<number | null>(null)
const percentTick = ref(0)

// 定时器相关
let blockTimer: NodeJS.Timer | null = null
let percentTimer: number | null = null

// 计算属性
const currentBlockNumber = computed(() => blockNumber.value ?? 0)
const expectBlockNumber = computed(() => {
  return props.item.expectBlockNumber || 0
})

// Withdraw step logic
const withdrawStep = computed(() => {
  // If iswithdrawed is true, both steps are completed
  if (props.item.iswithdrawed) {
    return 3
  }
  // If isReadyByBlockNumber is true, move to step 2
  if (props.item.isReadyByBlockNumber) {
    return 2
  }
  // Otherwise use the existing withdrawStep or default to 1
  return props.item.withdrawStep || 1
})

const withdrawStep1Done = computed(() => {
  return withdrawStep.value > 1
})

const withdrawStep2Done = computed(() => {
  return withdrawStep.value > 2
})

// 实时进度条计算
const withdrawStep1Bar = computed(() => {
  // 依赖 percentTick 触发重新计算
  void percentTick.value

  if (withdrawStep.value > 1)
    return 100
  if (withdrawStep.value === 1) {
    // Calculate progress based on current block vs expected block
    const startBlock = Number(props.item.block_number) || 0
    const expectedBlock = expectBlockNumber.value
    const current = currentBlockNumber.value

    if (expectedBlock <= startBlock)
      return 0
    if (current < startBlock)
      return 0
    if (current >= expectedBlock)
      return 100

    const percent = ((current - startBlock) / (expectedBlock - startBlock)) * 100
    return Math.max(0, Math.min(100, percent))
  }
  return 0
})

const withdrawStep2Bar = computed(() => {
  if (withdrawStep.value === 1)
    return 0
  if (Number(props.item.shortAmount) <= 0)
    return 100
  const totalAmount = Number(props.item.amount)
  const missingAmount = Number(props.item.shortAmount)
  if (totalAmount <= 0)
    return 0
  const progress = ((totalAmount - missingAmount) / totalAmount) * 100
  const maxProgress = Number(props.item.rank) > 0 ? 90 : 100
  return Math.max(0, Math.min(maxProgress, progress))
})

// 区块更新函数
async function updateBlockNumber() {
  try {
    const contractHelper = accountStore.contractHelper
    const res = await contractHelper?.getAstarBlockInfo()
    blockNumber.value = Number(res?.blockNumber)
  }
  catch (error) {
    console.error('Error updating block number:', error)
    blockNumber.value = null
  }
}

// 步骤切换逻辑
watch([currentBlockNumber], () => {
  if (currentBlockNumber.value >= expectBlockNumber.value) {
    // 这里可以触发步骤更新逻辑
    console.log('Block number reached:', currentBlockNumber.value)
  }
})

function handleDelete() {
  console.log('handleDelete')
  // 如果 withdrawStep2Done 为 true，记录到缓存
  if (withdrawStep2Done.value) {
    markAsDeleted()
  }
  isHidden.value = true
}

// 生命周期
onMounted(() => {
  // 检查是否已经被删除
  if (withdrawStep2Done.value && checkIfDeleted()) {
    isHidden.value = true
    return
  }

  // 初始化区块更新
  updateBlockNumber()
  blockTimer = setInterval(updateBlockNumber, 30000) // 每30秒更新一次

  // 初始化进度条更新
  percentTimer = window.setInterval(() => {
    percentTick.value++
  }, 60 * 1000) // 每分钟更新一次
})

onUnmounted(() => {
  // 清理定时器
  if (blockTimer) {
    clearInterval(blockTimer as unknown as number)
    blockTimer = null
  }
  if (percentTimer !== null) {
    clearInterval(percentTimer)
    percentTimer = null
  }
})
</script>

<template>
  <div v-if="!isHidden" class="max-w-[300px] rounded-[12px] border-2 border-black bg-white p-5">
    <!-- 当 withdrawStep2Done 为 true 时显示完成状态 -->
    <div v-if="withdrawStep2Done" class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <img
            class="h-6 w-6"
            :src="getImageUrl('token/stastr.svg')"
            alt=""
          />
          <span class="text-lg font-bold">{{ Number(props.item.amount).toFixed(2) }} wstASTR</span>
        </div>
        <i class="pi pi-times cursor-pointer" style="font-size: 1rem" @click="handleDelete"></i>
      </div>

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium">Standard Unstaking</span>
        </div>
        <button class="rounded-[8px] bg-green-100 px-3 py-1 text-xs font-medium text-green-600">
          Done
        </button>
      </div>
    </div>

    <!-- 原有的进行中状态 -->
    <div v-else>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <img
            class="h-6 w-6"
            :src="getImageUrl('token/stastr.svg')"
            alt=""
          />
          <span class="text-lg font-bold">{{ Number(props.item.amount).toFixed(2) }} wstASTR</span>
          <a v-if="props.item.exploreUrl" :href="props.item.exploreUrl" target="_blank">
            <i class="pi pi-external-link cursor-pointer text-xs font-bold text-[#2F80ED]"></i>
          </a>
        </div>
        <i class="pi pi-times cursor-pointer" style="font-size: 1rem" @click="handleDelete"></i>
      </div>

      <div class="mt-4 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium">Standard Unstaking</span>
        </div>
        <div class="rounded-[8px] px-3 py-1 text-xs font-medium" :class="withdrawStep === 3 ? 'bg-green-100 text-green-600' : 'bg-[#E9F7EF] text-[#11A24D]'">
          {{ withdrawStep === 3 ? 'Completed' : 'In Progress' }}
        </div>
      </div>

      <div class="mt-2 flex items-center text-sm font-medium text-gray-700">
        <span v-if="withdrawStep === 3">Withdraw completed</span>
        <span v-else>#{{ props.item.rank + 1 }} in Queue {{ withdrawStep === 1 ? `(Waiting for Block #${expectBlockNumber})` : `(Pending ${Number(props.item.shortAmount).toFixed(2)} ASTR)` }}</span>
        <i
          v-if="withdrawStep !== 1 && withdrawStep !== 3"
          v-tooltip.top="'Pending = your request + requests ahead in queue'"
          class="pi pi-info-circle ml-1 cursor-help text-gray-400"
        ></i>
      </div>

      <div class="mt-2">
        <div class="mx-[-10px] rounded bg-gray-100 p-3">
          <div class="flex cursor-pointer items-center justify-between text-sm" @click="stepOpen = !stepOpen">
            <span v-if="withdrawStep === 1">Waiting for Block <a href="https://astar.blockscout.com/" target="_blank" class="text-blue-500">#{{ expectBlockNumber }}</a></span>
            <span v-else>Pending Queue & Amount</span>
            <i :class="stepOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
          </div>
          <div v-if="stepOpen">
            <div class="mt-2">
              <div class="mb-1 flex items-center justify-between gap-2 text-xs">
                <span :class="withdrawStep === 1 ? 'font-bold' : ''">1. Waiting for Block #{{ expectBlockNumber }}</span>
                <span v-if="withdrawStep1Done" class="pi pi-check text-green-500"></span>
              </div>
              <ProgressBar :value="withdrawStep1Bar" :show-value="false" :pt="{ root: 'h-1', value: 'bg-[#11A24D]' }" />
              <div class="mt-1 text-xs text-gray-500">
                Current: #{{ currentBlockNumber }} | Target: #{{ expectBlockNumber }}
              </div>
            </div>
            <div class="mt-4">
              <div class="mb-1 flex items-center justify-between gap-2 text-xs">
                <span :class="withdrawStep === 2 ? 'font-bold' : ''">2. Pending Queue & Amount</span>
                <span v-if="withdrawStep2Done" class="pi pi-check text-green-500"></span>
              </div>
              <ProgressBar :value="withdrawStep2Bar" :show-value="false" :pt="{ root: 'h-1', value: 'bg-[#11A24D]' }" />
            </div>
          </div>
        </div>
      </div>

      <div class="mx-[-10px] mt-2 flex items-center rounded bg-gray-100 p-2 text-xs text-gray-600">
        <i class="pi pi-info-circle mr-1"></i>
        <span v-if="withdrawStep === 3">Withdraw has been completed successfully.</span>
        <span v-else-if="withdrawStep === 1">Waiting for block #{{ expectBlockNumber }} to be reached. Current: #{{ currentBlockNumber }}</span>
        <span v-else>You're #{{ props.item.rank + 1 }} in line. Unstaking will proceed automatically once the pool receives enough ASTR.</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less"></style>
